import FilterAltIcon from '@mui/icons-material/FilterAlt'
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import CustomChip from '@/components/Chip'
import { useLazyGetRecipeDetailQuery } from '@/store/apiService'
import { getRecipeLabelDetails } from '@/utils/helper'

import { ProjectStatus } from '../projects'

interface StyledSortLabelProps {
  label: string
  filters?: ProjectStatus[]
  setFilters?: (filters: ProjectStatus[]) => void
  ordering?: string
  setOrdering?: (field: string) => void
  caseId?: string
}

const columnKeyMap: Record<string, string> = {
  Name: 'name',
  Type: 'type',
  'Created At': 'created_ts',
  'Created By': 'created_by',
}

const RecipeDetailHeader = ({
  label,
  filters = [],
  setFilters,
  ordering,
  setOrdering,
  caseId,
}: StyledSortLabelProps) => {
  const isStatus = label === 'Type'
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const field = columnKeyMap[label] ?? ''
  const isActive = ordering?.includes(field)
  const isAsc = isActive && !ordering?.startsWith('-')

  const handleSortClick = () => {
    if (!field || !setOrdering) return
    setOrdering(isAsc ? `-${field}` : field)
  }

  const [trigger, { data: recipeData }] = useLazyGetRecipeDetailQuery()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    if (caseId) {
      trigger({ paginated: false, caseId })
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleToggle = (workflow: string | null | undefined, type: string) => {
    if (!setFilters) return
    const key = workflow ? `${workflow}:${type}` : type
    const newFilters = filters.includes(key)
      ? filters.filter((f) => f !== key)
      : [...filters, key]
    setFilters(newFilters)
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <TableSortLabel
        active
        direction={isAsc ? 'asc' : 'desc'}
        onClick={handleSortClick}
        sx={{
          color: 'white',
          '& .MuiTableSortLabel-icon': {
            color: 'white !important',
          },
        }}
      >
        {label}
      </TableSortLabel>

      {isStatus && (
        <>
          <IconButton onClick={handleClick} size="small">
            <FilterAltIcon sx={{ fontSize: 18, color: 'white' }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                backgroundColor: '#1e1e1e',
                color: 'white',
                // width: '300px',
                maxHeight: '400px',
              },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {[
              ...new Map(
                recipeData?.map((item: any) => {
                  const key = item.workflow
                    ? `${item.workflow}:${item.type}`
                    : item.type
                  return [key, item]
                }),
              ).values(),
            ].map((item: any) => {
              const key = item.workflow
                ? `${item.workflow}:${item.type}`
                : item.type
              const isChecked = filters.includes(key)

              const { workflowColor, labelText } = getRecipeLabelDetails({
                workflow: item.workflow,
                type: item.type,
              })

              return (
                <MenuItem
                  key={item.id}
                  onClick={() => handleToggle(item.workflow, item.type)}
                  disableRipple
                  sx={{
                    paddingLeft: '8px',
                    paddingRight: '16px',
                    py: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 'auto',
                    gap: '9px',
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    sx={{
                      p: '9px',
                      color: '#90caf9',
                      '&.Mui-checked': { color: '#90caf9' },
                      height: 20,
                      width: 20,
                    }}
                  />
                  <Typography
                    sx={{
                      lineHeight: '24px',
                      fontSize: '15px',
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: '4px',
                    }}
                  >
                    <CustomChip
                      label={labelText}
                      size="medium"
                      sx={{
                        bgcolor: workflowColor,
                        color: 'white',
                        fontWeight: 400,
                        fontSize: '13px',
                        lineHeight: '18px',
                        letterSpacing: '0.16px',
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '&:hover': {
                          bgcolor: workflowColor,
                        },
                      }}
                    />
                  </Typography>
                </MenuItem>
              )
            })}
          </Menu>
        </>
      )}
    </Box>
  )
}

export default RecipeDetailHeader
